import UserProfileHeader from "Components/Pages/UserProfile/UserProfile/UserProfileHeader/UserProfileHeader";
import React, { ChangeEvent } from "react";
import { DPErrorMessage, DPPageWrapper, DPTabs } from "Components/DP";
import pretty from "Shared/pretty";
import { Redirect, Route, Switch } from "react-router";
import {
  StatementsTab,
  AccountDetailsTab,
  TabLabels,
  UserProfileTabStrings,
} from "./UserProfileTabs";
import DisbursementsTab from "./UserProfileTabs/DisbursementsTab/DisbursementsTab";
import RewardsTab from "./UserProfileTabs/RewardsTab/RewardsTab";
import { useEmployeeProfileQuery } from "Hooks/useEmployeeProfileQueryContext";
import { usePartnerConfig } from "Hooks/usePartnerConfig";
import useDisbursements from "./UserProfileTabs/DisbursementsTab/Hooks/useDisbursementsHook";

type UserProfileProps = {
  tabIndex: number;
  handleChangeTab: (label: string, value: number) => void;
  employeeProfileId: string;
};

const UserProfile = ({ tabIndex, handleChangeTab, employeeProfileId }: UserProfileProps) => {
  const { permissions, rewardsOptions, offCyclePaymentOptions } = usePartnerConfig();
  const { userDetails, error } = useEmployeeProfileQuery();
  const { data: disbursementsData } = useDisbursements(employeeProfileId, !permissions.canListDisbursements);

  if (!userDetails || error)
    return (
      <DPPageWrapper title="Error">
        <DPErrorMessage />
      </DPPageWrapper>
    );
  const { employee, providerAccounts } = userDetails;
  const {
    firstName,
    lastName,
    hasPlaceholderEmail,
    currentPayPeriodSummary,
  } = employee;

  const hideDisbursementsTab =
    !permissions.canListDisbursements ||
    hasPlaceholderEmail ||
    !offCyclePaymentOptions.enableOffCycleDisbursements ||
    (!disbursementsData?.employeeProfile.disbursements.totalCount && !permissions.canCreateDisbursements);

  const hideRewardsTab = !rewardsOptions?.enableRewards;

  const providerAccountsWithPayPeriods = providerAccounts.items.filter(
    providerAccount => providerAccount.payPeriods?.items.length
  );

  const disabledTabs = !providerAccountsWithPayPeriods.length
    ? { [UserProfileTabStrings.statements]: "User does not have any pay periods." }
    : null;

  let tabLabelsCopy = [...TabLabels];
  if (hideDisbursementsTab) {
    tabLabelsCopy = tabLabelsCopy.filter(label => label !== "CYCLE");
  }
  if (hideRewardsTab) {
    tabLabelsCopy = tabLabelsCopy.filter(label => label !== "REWARD");
  }

  const handleChange = (evt: ChangeEvent<unknown>, value: number) => {
    handleChangeTab(tabLabelsCopy[value], value);
  };

  return (
    <DPPageWrapper title={pretty.fullName(firstName!, lastName!)}>
      <UserProfileHeader
        firstName={firstName!}
        lastName={lastName!}
        balance={
          (currentPayPeriodSummary &&
            currentPayPeriodSummary.availableBalanceCents) ||
          0
        }
        hasMultipleProviders={userDetails.employee.providers.totalCount > 1}
      />
      <DPTabs
        labels={tabLabelsCopy}
        tabIndex={tabIndex}
        onChange={handleChange}
        disabledTabs={disabledTabs}
      >
        <Switch>
          <Route
            path="/users/:id/statements"
            render={() => (
              <StatementsTab
                providerAccounts={providerAccountsWithPayPeriods}
                employeeProfileId={userDetails.id}
              />
            )}
          />
          <Route
            path="/users/:id/details"
            render={() => <AccountDetailsTab {...userDetails} />}
          />
          {!hideDisbursementsTab && (
            <Route
              component={DisbursementsTab}
              path="/users/:id/disbursements"
            />
          )}
          {!hideRewardsTab && (
            <Route
              component={RewardsTab}
              path="/users/:id/rewards"
            />
          )}
          <Redirect to="/users/:id/details" />
        </Switch>
      </DPTabs>
    </DPPageWrapper>
  );
};

export default UserProfile;
